import { ResolvedFormula } from '@payflip/types/product-builder';
import { monthDiff } from '@payflip/utils';
import { Parser, Value } from 'expr-eval';
import { FunctionalHOC } from '../renderer';

export const withResolvedFormulas: FunctionalHOC = (Component) => {
  return (props) => {
    const resolvedFormulas = checkAndResolve({ ...props }, {} as typeof props);

    const mergedProps = {
      ...props,
      ...resolvedFormulas
    };

    return <Component {...mergedProps} />;
  };
};

function checkAndResolve<T extends { [K in keyof T]: T[K] }>(
  obj: T,
  seed: T
): T {
  const resolvedFormulas = Object.entries(obj).reduce((acc, [key, value]) => {
    if (isResolvedFormula(value)) {
      return { ...acc, [key]: resolve(value) };
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const nestedResult = checkAndResolve({ ...value }, {});
      return { ...acc, [key]: { ...nestedResult } };
    }

    return { ...acc, [key]: value };
  }, seed);

  return resolvedFormulas;
}

function resolve(value: ResolvedFormula): number {
  const parser = new Parser();

  parser.functions.MONTH_DIFF = (a: string, b: string): number => {
    return Math.abs(monthDiff(new Date(a), new Date(b)));
  };

  const result = parser.evaluate(value.formula, value.inputs as Value);

  return Math.round(result * 100) / 100;
}

function isResolvedFormula(
  prop: ResolvedFormula | unknown
): prop is ResolvedFormula {
  return (
    typeof prop === 'object' &&
    prop !== null &&
    Object.keys(prop).length === 2 &&
    'formula' in prop &&
    'inputs' in prop
  );
}
